import React, { useEffect, useState, useRef } from 'react';
import { useStorageStore } from '../store/useStorageStore';
import { useDanceStore, calculateGroundedElevation, defaultPose } from '../store/useDanceStore';
import { TUTORIAL_MODULES } from '../utils/tutorialData';
import { X, GraduationCap, MousePointer2, RotateCcw, Copy, ArrowLeftRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { trackTutorialEvent } from '../lib/analytics';
import { JointRotation } from '../types';

export const TutorialOverlay: React.FC = () => {
    const { t } = useTranslation();
    const tutorialProgress = useStorageStore(state => state.tutorialProgress);
    const updateTutorialProgress = useStorageStore(state => state.updateTutorialProgress);
    const keyframes = useDanceStore(state => state.keyframes);
    const selectedJoint = useDanceStore(state => state.selectedJoint);
    const currentTime = useDanceStore(state => state.currentTime);
    const currentPose = useDanceStore(state => state.currentPose);
    const isPosing = useDanceStore(state => state.isPosing);
    const activeAxis = useDanceStore(state => state.activeAxis);
    const isPlaying = useDanceStore(state => state.isPlaying);
    const hasUnsavedSequenceChanges = useDanceStore(state => state.hasUnsavedSequenceChanges);
    const showLabels = useDanceStore(state => state.showLabels);
    const showAxes = useDanceStore(state => state.showAxes);
    const loadedSequence = useDanceStore(state => state.loadedSequence);
    const confirmDialog = useDanceStore(state => state.confirmDialog);
    const inputDialog = useDanceStore(state => state.inputDialog);
    const isSettingsOpen = useDanceStore(state => state.isSettingsOpen);
    const isFeedbackOpen = useDanceStore(state => state.isFeedbackOpen);
    const setDuration = useDanceStore(state => state.setDuration);
    const resetPose = useDanceStore(state => state.resetPose);
    const clearTimeline = useDanceStore(state => state.clearTimeline);
    const footMarkers = useDanceStore(state => state.footMarkers);
    const locationMarkers = useDanceStore(state => state.locationMarkers);
    const lastModuleIdRef = useRef<string | null>(tutorialProgress.currentModuleId);
    const ignoreNextMirrorToggleRef = useRef(false);

    const isMirroring = useDanceStore(state => state.isMirroring);
    const duration = useDanceStore(state => state.duration);
    const timelineZoom = useDanceStore(state => state.timelineZoom);
    const isTimelineCompact = useDanceStore(state => state.isTimelineCompact);
    const metronomeInterval = useDanceStore(state => state.metronomeInterval);
    const copiedFrames = useDanceStore(state => state.copiedFrames);
    const selectedFrameTimes = useDanceStore(state => state.selectedFrameTimes);
    const frameMarkers = useDanceStore(state => state.frameMarkers);
    const history = useDanceStore(state => state.history);

    const hasMusicLoaded = useDanceStore(state => state.hasMusicLoaded);
    const beatSubdivision = useDanceStore(state => state.beatSubdivision);
    const musicVolume = useDanceStore(state => state.musicVolume);

    const folders = useStorageStore(state => state.folders);
    const poses = useStorageStore(state => state.poses);
    const activeModule = useDanceStore(state => state.activeModule);
    const actingSource = useDanceStore(state => state.actingSource);

    const setActiveModule = useDanceStore(state => state.setActiveModule);
    const newSequence = useDanceStore(state => state.newSequence);
    const confirmAction = useDanceStore(state => state.confirmAction);

    const currentModule = TUTORIAL_MODULES.find(m => m.id === tutorialProgress.currentModuleId);
    const rawStep = currentModule?.steps[tutorialProgress.currentStepIndex];
    const currentStep = React.useMemo(() => {
        if (!rawStep) return null;
        if (rawStep.id === 'seq-copy-keyframe') {
            const movedKeyframe = keyframes.find(kf => kf.time > 0.0);
            if (movedKeyframe) {
                return {
                    ...rawStep,
                    targetId: `timeline-frame-${movedKeyframe.time.toFixed(3)}`
                };
            }
        }
        return rawStep;
    }, [rawStep, keyframes]);

    const isFinalStep = React.useMemo(() => {
        return !!currentModule && tutorialProgress.currentStepIndex === currentModule.steps.length - 1;
    }, [currentModule, tutorialProgress.currentStepIndex]);

    const nextModule = React.useMemo(() => {
        const currentIndex = TUTORIAL_MODULES.findIndex(m => m.id === tutorialProgress.currentModuleId);
        if (currentIndex === -1) return null;
        for (let i = currentIndex + 1; i < TUTORIAL_MODULES.length; i++) {
            if (TUTORIAL_MODULES[i].steps.length > 0) {
                return TUTORIAL_MODULES[i];
            }
        }
        return null;
    }, [tutorialProgress.currentModuleId]);

    const startNextModule = (nextModuleId: string) => {
        const start = () => {
            newSequence();
            updateTutorialProgress({
                currentModuleId: nextModuleId,
                currentStepIndex: 0
            });
            setActiveModule('PoseDesigner');
        };

        if (keyframes.length > 0 || hasUnsavedSequenceChanges) {
            confirmAction({
                title: t('tutorial.new_tutorial_title', { defaultValue: 'New Tutorial Sequence' }),
                message: t('tutorial.new_tutorial_message', { defaultValue: 'Starting this tutorial will clear your current sequence. Do you want to continue?' }),
                onConfirm: start
            });
        } else {
            start();
        }
    };

    const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
    const [targetRects, setTargetRects] = useState<DOMRect[]>([]);
    const [supportingRects, setSupportingRects] = useState<DOMRect[]>([]);
    const [frozenTooltipPos, setFrozenTooltipPos] = useState<{ top: number; left: number; placement: 'top' | 'bottom' | 'left' | 'right' } | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isPositionStable, setIsPositionStable] = useState(false);
    const [displayedStep, setDisplayedStep] = useState(currentStep);
    const [isContentVisible, setIsContentVisible] = useState(true);
    const [isIndicatorsVisible, setIsIndicatorsVisible] = useState(false);
    const simulatedStepsRef = useRef<string | null>(null);

    const [tooltipHeight, setTooltipHeight] = useState(160);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const hasVisual = !!currentStep && (
        currentStep.actionRequired === 'rotateJoint' ||
        currentStep.actionRequired === 'rotateJointMouse' ||
        currentStep.actionRequired === 'rotateConicalMouse' ||
        currentStep.actionRequired === 'rotateTorsoMouse' ||
        currentStep.actionRequired === 'moveFloorMouse' ||
        currentStep.actionRequired === 'changeElevation' ||
        currentStep.id === 'wm-scroll-rotation' ||
        currentStep.id === 'mark-location-shortcut'
    );

    const advanceStep = () => {
        const nextStepIndex = tutorialProgress.currentStepIndex + 1;
        const totalSteps = currentModule?.steps.length || 0;

        if (nextStepIndex < totalSteps) {
            if (currentModule) {
                trackTutorialEvent('step_finished', { name: currentModule.title, step: tutorialProgress.currentStepIndex + 1 });
            }
            const updates: any = { currentStepIndex: nextStepIndex };

            // If the next step is the LAST one (Conclusion), mark as completed now
            if (nextStepIndex === totalSteps - 1) {
                if (!tutorialProgress.completedModuleIds.includes(currentModule!.id)) {
                    updates.completedModuleIds = [...tutorialProgress.completedModuleIds, currentModule!.id];
                    trackTutorialEvent('finished', { name: currentModule!.title });
                }
            }
            updateTutorialProgress(updates);
        } else {
            // This case might still be reached if multiple things trigger advanceStep simultaneously
            setDuration(10);
            updateTutorialProgress({
                currentModuleId: null,
                currentStepIndex: 0,
                completedModuleIds: tutorialProgress.completedModuleIds.includes(currentModule!.id)
                    ? tutorialProgress.completedModuleIds
                    : [...tutorialProgress.completedModuleIds, currentModule!.id]
            });
        }
    };

    // Read height of tooltip once rendered to adjust positioning
    useEffect(() => {
        if (tooltipRef.current) {
            const h = tooltipRef.current.offsetHeight;
            if (isVisible && Math.abs(h - tooltipHeight) > 10) {
                setTooltipHeight(h);
            }
        }
    }, [currentStep, isVisible, tooltipHeight, displayedStep]);

    // Track initial state for "change" detection
    const initialJointPoseRef = useRef<[number, number, number] | null>(null);
    const initialMannequinPosRef = useRef<[number, number, number] | null>(null);
    const movementDetectedThisStepRef = useRef(false);

    // Reset frozen position and movement detection on step change
    useEffect(() => {
        if (!currentStep) return;

        // Close any active context menu during step transitions to prevent overlap
        useDanceStore.getState().closeContextMenu();

        // When the actual step in the store changes, we start a transition
        setIsContentVisible(false);
        setIsPositionStable(false);
        setIsIndicatorsVisible(false);

        const timer = setTimeout(() => {
            setDisplayedStep(currentStep);
            setIsContentVisible(true);
        }, 300);

        // Show indicators (lines/circles) only after the tooltip has settled and content is visible
        const indicatorTimer = setTimeout(() => {
            setIsIndicatorsVisible(true);
        }, 1000);

        // Safety timeout: If target isn't found within 1.5s, show it anyway at fallback position
        const safetyTimer = setTimeout(() => {
            setIsPositionStable(true);
        }, 1500);

        movementDetectedThisStepRef.current = false;
        setFrozenTooltipPos(null);

        if (currentStep.actionRequired === 'rotateJointMouse' || currentStep.actionRequired === 'rotateConicalMouse' || currentStep.actionRequired === 'rotateTorsoMouse') {
            let jointName = currentStep.targetValue;
            if (currentStep.id === 'adv-drag-elbow-drag') {
                jointName = 'leftShoulder';
            }
            if (jointName && currentPose[jointName]) {
                initialJointPoseRef.current = [...(currentPose[jointName] as [number, number, number])];
            }
        } else if (currentStep.actionRequired === 'moveFloorMouse' || currentStep.actionRequired === 'resetJoint' || currentStep.actionRequired === 'changeElevation') {
            if (currentPose.elevation) {
                initialMannequinPosRef.current = [...currentPose.elevation];
            }
        } else {
            initialJointPoseRef.current = null;
            initialMannequinPosRef.current = null;
        }

        return () => {
            clearTimeout(timer);
            clearTimeout(indicatorTimer);
            clearTimeout(safetyTimer);
        };
    }, [tutorialProgress.currentStepIndex, tutorialProgress.currentModuleId]);

    // Side effect: Spread legs before foot marking step in Module 2
    useEffect(() => {
        if (currentStep?.id === 'mark-foot' && currentModule?.id === 'locations-markers') {
            const anglesL = (currentPose.leftHip || [0, 0, 0]) as [number, number, number];
            const anglesR = (currentPose.rightHip || [0, 0, 0]) as [number, number, number];

            // If they aren't already spread, apply a wider stance so markers don't overlap vertically
            const isSpread = Math.abs(anglesL[2] - 35) < 1 && Math.abs(anglesR[2] + 35) < 1;

            if (!isSpread) {
                const newPose = { ...currentPose };
                newPose.leftHip = [0, 0, 35];
                newPose.rightHip = [0, 0, -35];

                // Re-ground the mannequin at this wider stance
                const groundElev = calculateGroundedElevation(newPose);
                newPose.elevation = [currentPose.elevation?.[0] || 0, groundElev, currentPose.elevation?.[2] || 0];

                useDanceStore.setState({
                    currentPose: newPose,
                    groundedElevation: groundElev
                });
            }
        }
    }, [currentStep?.id, currentModule?.id]);

    // Side effect: Move mannequin away before snap tutorial step in Module 2
    useEffect(() => {
        if ((currentStep?.id === 'snap-to-marker' || currentStep?.id === 'mark-foot' || currentStep?.id === 'mark-location-shortcut') && currentModule?.id === 'locations-markers') {
            const timer = setTimeout(() => {
                const state = useDanceStore.getState();
                const pos = (state.currentPose.elevation || [0, 0, 0]) as [number, number, number];
                const dist = Math.sqrt(pos[0] ** 2 + pos[2] ** 2);

                // If still near the center/markers, move it away to demonstrate snapping
                if (dist < 3) {
                    const newPose = { ...state.currentPose };
                    newPose.elevation = [pos[0], pos[1], pos[2] - 6];
                    const groundElev = calculateGroundedElevation(newPose);
                    useDanceStore.setState({
                        currentPose: newPose,
                        groundedElevation: groundElev
                    });
                }
            }, 600);
            return () => clearTimeout(timer);
        }
    }, [currentStep?.id, currentModule?.id]);

    // Side effect: Pre-select a marker for snap tutorial step in Module 2
    useEffect(() => {
        if (currentStep?.id === 'snap-to-marker' && currentModule?.id === 'locations-markers') {
            const state = useDanceStore.getState();
            if (state.locationMarkers.length > 0) {
                useDanceStore.setState({
                    selectedLocationMarkerLabel: state.locationMarkers[state.locationMarkers.length - 1].label,
                    selectedFootMarkerLabel: null
                });
            } else if (state.footMarkers.length > 0) {
                useDanceStore.setState({
                    selectedFootMarkerLabel: state.footMarkers[0].label,
                    selectedLocationMarkerLabel: null
                });
            }
        }
    }, [currentStep?.id, currentModule?.id]);

    // Side effect: Joint selection and posing for Posing/Advanced Posing tutorials
    useEffect(() => {
        if (currentModule?.id === 'posing') {
            if (currentStep?.id === 'adv-move-asymmetric') {
                useDanceStore.getState().setSelectedJoint('leftShoulder');
            } else if (currentStep?.id === 'adv-reflect') {
                useDanceStore.getState().setSelectedJoint('rightShoulder');
            } else if (currentStep?.id === 'adv-mirror-mode') {
                const state = useDanceStore.getState();
                if (state.showLabels) {
                    state.setShowLabels(false);
                }
                if (!state.isMirroring) {
                    ignoreNextMirrorToggleRef.current = true;
                    state.setIsMirroring(true);
                }
            }
        }
        if (currentModule?.id === 'advanced-posing') {
            if (currentStep?.id === 'adv-torso-intro' || currentStep?.id === 'adv-torso-t' || currentStep?.id === 'adv-torso-v' || currentStep?.id === 'adv-torso-l') {
                useDanceStore.getState().setSelectedJoint('torso');
            } else if (currentStep?.id === 'adv-drag-relationship-intro') {
                useDanceStore.getState().setSelectedJoint('leftElbow');
            }
        }
        if (currentModule?.id === 'workspace-mastery') {
            if (currentStep?.id === 'wm-scroll-rotation') {
                useDanceStore.getState().setSelectedJoint('rightShoulder');
            }
        }
    }, [currentStep?.id, currentModule?.id]);

    // Side effect: Automatically select the target frame in the Sequence Design tutorial
    useEffect(() => {
        if (currentModule?.id === 'quick-editing-timeline') {
            if (currentStep?.id === 'seq-move-keyframe') {
                const state = useDanceStore.getState();
                state.setCurrentTime(1.0);
                state.toggleFrameSelection(1.0, false);
            }
        }
    }, [currentStep?.id, currentModule?.id]);
    // Side effect: Reset pose and camera before Step 7 and Step 8 of Attitudes module
    useEffect(() => {
        if (currentModule?.id === 'attitudes') {
            if (currentStep?.id === 'vertical-attitude-conical' || currentStep?.id === 'torso-rotation') {
                const state = useDanceStore.getState();
                state.resetPose();
                state.resetCamera();
            }
        }
    }, [currentStep?.id, currentModule?.id]);

    // Side effect: Seed Arabesque and select Unsorted folder for Library Management and Context Shortcuts tutorials
    useEffect(() => {
        if (currentModule?.id === 'library-management' || currentModule?.id === 'context-shortcuts') {
            const storageState = useStorageStore.getState();

            // Ensure the Unsorted folder ('default-poses') is selected when switching to Poses tab, talking about folders, or loading poses
            if (
                currentStep?.id === 'lib-switch-tab' ||
                currentStep?.id === 'lib-create-folder' ||
                currentStep?.id === 'lib-load-pose' ||
                currentStep?.id === 'shortcuts-folders' ||
                currentStep?.id === 'shortcuts-library'
            ) {
                storageState.setSelectedPoseFolderId(null);
            }

            if (currentStep?.id === 'shortcuts-folders' || currentStep?.id === 'shortcuts-library') {
                useDanceStore.getState().setActiveModule('Poses');
            }

            if (currentStep?.id === 'lib-load-pose' || currentStep?.id === 'shortcuts-library') {
                // Seed Arabesque pose if not already present in the library
                const hasArabesque = storageState.poses.some(p => p.id === 'mock-pose-arabesque');
                if (!hasArabesque) {
                    const mockPose = {
                        id: 'mock-pose-arabesque',
                        name: 'Arabesque',
                        folderId: null,
                        pose: {
                            ...defaultPose,
                            leftHip: [0, 0, 45] as [number, number, number],
                            leftKnee: [0, 30, 0] as [number, number, number]
                        }
                    };
                    useStorageStore.setState({
                        poses: [...storageState.poses, mockPose]
                    });
                }
            }
        }
    }, [currentStep?.id, currentModule?.id]);

    // Auto-reset when starting ANY module
    useEffect(() => {
        if (tutorialProgress.currentModuleId && tutorialProgress.currentModuleId !== lastModuleIdRef.current) {
            const moduleName = TUTORIAL_MODULES.find(m => m.id === tutorialProgress.currentModuleId)?.title || 'Unknown';
            trackTutorialEvent('started', { name: moduleName });
            clearTimeline();
            resetPose();

            // Clean Room resets
            const danceState = useDanceStore.getState();
            if (tutorialProgress.currentModuleId === 'advanced-posing') {
                danceState.setSelectedJoint('torso');
            } else {
                danceState.setSelectedJoint(null);
            }

            if (tutorialProgress.currentModuleId !== 'designing-sequences-with-music') {
                danceState.removeMusic();
            }

            useDanceStore.setState({
                duration: tutorialProgress.currentModuleId === 'introduction' ? 3.5 : 10,
                metronomeInterval: 0.5,
                timelineZoom: 1.0,
                isTimelineCompact: false,
                isMirroring: true,
                librarySelection: [],
                beatSubdivision: 1
            });

            if (tutorialProgress.currentModuleId === 'library-management') {
                useDanceStore.setState({ activeModule: 'Poses' });
                const storageState = useStorageStore.getState();
                storageState.setSelectedPoseFolderId(null);
                storageState.setSelectedSequenceFolderId(null);
            }

            if (tutorialProgress.currentModuleId === 'context-shortcuts') {
                useDanceStore.setState({ activeModule: 'PoseDesigner' });
            }

            if (
                tutorialProgress.currentModuleId === 'designing-sequences-no-music' ||
                tutorialProgress.currentModuleId === 'quick-editing-timeline' ||
                tutorialProgress.currentModuleId === 'notes-annotations'
            ) {
                // Pre-populate timeline with keyframes at 0.0s and 1.0s to avoid reteaching Module 1 basics (saving/posing)
                const kf0 = {
                    time: 0.0,
                    pose: { ...defaultPose },
                };
                const kf1 = {
                    time: 1.0,
                    pose: {
                        ...defaultPose,
                        leftHip: [0, 0, 35] as [number, number, number]
                    }
                };
                useDanceStore.setState({
                    keyframes: [kf0, kf1],
                    duration: 10,
                    metronomeInterval: 1.0
                });
            }

            if (tutorialProgress.currentModuleId === 'notes-annotations') {
                useDanceStore.getState().setMinimizedSidebar('textToPose', false);
            }
        }
        lastModuleIdRef.current = tutorialProgress.currentModuleId;
    }, [tutorialProgress.currentModuleId, clearTimeline, resetPose]);

    // Cleanup mock poses/folders and restore standard editor settings when exiting ANY module
    useEffect(() => {
        if (!tutorialProgress.currentModuleId && lastModuleIdRef.current) {
            // Close any open context menu upon exiting
            useDanceStore.getState().closeContextMenu();

            // We just exited a module!
            if (lastModuleIdRef.current === 'library-management' || lastModuleIdRef.current === 'context-shortcuts') {
                // Delete the mock Arabesque pose
                const storageState = useStorageStore.getState();
                const hasArabesque = storageState.poses.some(p => p.id === 'mock-pose-arabesque');
                if (hasArabesque) {
                    useStorageStore.setState({
                        poses: storageState.poses.filter(p => p.id !== 'mock-pose-arabesque')
                    });
                }
            }
        }
    }, [tutorialProgress.currentModuleId]);

    // Detect action completion
    const prevKeyframesLength = useRef(keyframes.length);
    const prevIsPlaying = useRef(isPlaying);
    const prevLoadedSequenceId = useRef(loadedSequence?.id);
    const prevIsMirroring = useRef(isMirroring);
    const prevShowLabels = useRef(showLabels);
    const prevShowAxes = useRef(showAxes);
    const prevFootMarkersLength = useRef(footMarkers.length);
    const prevLocationMarkersLength = useRef(locationMarkers.length);
    const prevHistoryLength = useRef(0);

    const prevDuration = useRef(duration);
    const prevTimelineZoom = useRef(timelineZoom);
    const prevIsTimelineCompact = useRef(isTimelineCompact);
    const prevMetronomeInterval = useRef(metronomeInterval);
    const prevCopiedFramesLength = useRef(copiedFrames.length);
    const prevSelectedFrameTimesLength = useRef(selectedFrameTimes.length);
    const getFrameMarkersCount = (markers: Record<string, number[]>) => {
        return Object.values(markers).reduce((acc, curr) => acc + (curr || []).length, 0);
    };
    const prevFrameMarkersCount = useRef(getFrameMarkersCount(frameMarkers));

    const prevHasMusicLoaded = useRef(hasMusicLoaded);
    const prevBeatSubdivision = useRef(beatSubdivision);
    const prevMusicVolume = useRef(musicVolume);

    const prevFoldersLength = useRef(folders.length);
    const prevPosesLength = useRef(poses.length);
    const prevActiveModule = useRef(activeModule);

    useEffect(() => {
        if (!currentStep) return;

        // Detect changeTorsoT action
        if (currentStep.actionRequired === 'changeTorsoT') {
            const elevation = currentPose.elevation || [0, 0, 0];
            const thrustVal = Math.round(Math.max(-180, Math.min(180, (elevation[2] / 1.44) * 180)));
            if (Math.abs(thrustVal) > 10 && !isPosing) {
                useDanceStore.getState().resetPose();
                advanceStep();
            }
        }

        // Detect changeTorsoV action
        if (currentStep.actionRequired === 'changeTorsoV') {
            const leftHip = currentPose.leftHip || [0, 0, 0];
            const verticalVal = Math.round(Math.max(0, Math.min(100, (-leftHip[0] / 180) * 100)));
            if (verticalVal > 5 && !isPosing) {
                useDanceStore.getState().resetPose();
                advanceStep();
            }
        }

        // Detect changeTorsoL action
        if (currentStep.actionRequired === 'changeTorsoL') {
            const pelvis = currentPose.pelvis || [0, 0, 0];
            const MAX_PELVIC_TILT = 30;
            const lateralVal = Math.round(Math.max(-100, Math.min(100, (pelvis[2] / MAX_PELVIC_TILT) * 100)));
            if (Math.abs(lateralVal) > 10 && !isPosing) {
                advanceStep();
            }
        }

        // Detect writePoseNote action
        if (currentStep.actionRequired === 'writePoseNote') {
            const notes = currentPose.notes || '';
            if (notes.trim().length > 0) {
                advanceStep();
            }
        }

        // Detect savePoseNote action
        if (currentStep.actionRequired === 'savePoseNote') {
            const kf = keyframes.find(k => Math.abs(k.time - currentTime) < 0.05);
            if (kf?.pose?.notes && kf.pose.notes.trim().length > 0) {
                advanceStep();
            }
        }

        // Detect openFrameNoteInput action
        if (currentStep.actionRequired === 'openFrameNoteInput') {
            const textarea = document.getElementById('keyframe-note-textarea');
            if (textarea) {
                advanceStep();
            }
        }

        // Detect saveFrameNote action
        if (currentStep.actionRequired === 'saveFrameNote') {
            const kf = keyframes.find(k => Math.abs(k.time - 4.0) < 0.05);
            if (kf?.note && kf.note.trim().length > 0 && !document.getElementById('keyframe-note-textarea')) {
                advanceStep();
            }
        }

        // Detect hoverFrame action
        if (currentStep.actionRequired === 'hoverFrame') {
            const tooltip = document.querySelector('.pose-preview-popup');
            if (tooltip) {
                advanceStep();
            }
        }

        // Detect changeDuration action
        if (currentStep.actionRequired === 'changeDuration' && duration !== prevDuration.current) {
            if (duration === 12) {
                advanceStep();
            }
        }

        // Detect changeTickInterval action
        if (currentStep.actionRequired === 'changeTickInterval' && metronomeInterval !== prevMetronomeInterval.current) {
            if (metronomeInterval === 0.5) {
                advanceStep();
            }
        }

        // Detect zoomIn action
        if (currentStep.actionRequired === 'zoomIn' && timelineZoom > prevTimelineZoom.current) {
            advanceStep();
        }

        // Detect zoomOut action
        if (currentStep.actionRequired === 'zoomOut' && timelineZoom < prevTimelineZoom.current) {
            advanceStep();
        }

        // Detect resetZoom action
        if (currentStep.actionRequired === 'resetZoom' && Math.abs(timelineZoom - 1.0) < 0.05 && Math.abs(prevTimelineZoom.current - 1.0) > 0.05) {
            advanceStep();
        }

        // Detect toggleCompact action
        if (currentStep.actionRequired === 'toggleCompact' && isTimelineCompact !== prevIsTimelineCompact.current) {
            advanceStep();
        }

        // Detect toggleFrameMarker action
        if (currentStep.actionRequired === 'toggleFrameMarker') {
            const count = getFrameMarkersCount(frameMarkers);
            if (count > prevFrameMarkersCount.current) {
                advanceStep();
            }
        }

        // Detect moveKeyframe action (resilient to custom snap targets)
        if (currentStep.actionRequired === 'moveKeyframe') {
            const hasPoseAt1 = keyframes.some(kf => Math.abs(kf.time - 1.0) < 0.05);
            if (!hasPoseAt1 && keyframes.length === 2) {
                advanceStep();
            }
        }

        // Detect copyKeyframe action (resilient to custom copy destinations)
        if (currentStep.actionRequired === 'copyKeyframe') {
            if (keyframes.length > prevKeyframesLength.current) {
                advanceStep();
            }
        }

        // Detect selectMultipleFrames action
        if (currentStep.actionRequired === 'selectMultipleFrames' && selectedFrameTimes.length > 1) {
            advanceStep();
        }

        // Detect moveMultipleFrames action
        if (currentStep.actionRequired === 'moveMultipleFrames') {
            const history = useDanceStore.getState().history;
            const lastAction = history[history.length - 1]?.actionDescription;
            if (lastAction === 'Move Multiple Keyframes' || lastAction === 'Ripple Move Multiple Keyframes') {
                advanceStep();
            }
        }

        // Detect clearTimeline action
        if (currentStep.actionRequired === 'clearTimeline' && keyframes.length === 0 && prevKeyframesLength.current > 0) {
            advanceStep();
        }

        // Detect toggleLabels action
        if (currentStep.actionRequired === 'toggleLabels' && showLabels !== prevShowLabels.current) {
            advanceStep();
        }

        // Detect toggleAxes action
        if (currentStep.actionRequired === 'toggleAxes' && showAxes !== prevShowAxes.current) {
            advanceStep();
        }

        // Detect toggleMirror action
        if (currentStep.actionRequired === 'toggleMirror' && isMirroring !== prevIsMirroring.current) {
            if (ignoreNextMirrorToggleRef.current) {
                ignoreNextMirrorToggleRef.current = false;
            } else {
                advanceStep();
            }
        }

        // Detect mirrorFlip or reflectJoint actions (both modify history)
        if ((currentStep.actionRequired === 'mirrorFlip' || currentStep.actionRequired === 'reflectJoint') &&
            useDanceStore.getState().history.length > prevHistoryLength.current) {
            advanceStep();
        }

        // Detect resetPose action
        if (currentStep.actionRequired === 'resetPose') {
            const isAllDefault = Object.keys(defaultPose).every(key => {
                if (key === 'elevation' || key === 'orientation') return true;
                const axes = currentPose[key] as [number, number, number];
                const defAxes = (defaultPose as any)[key] as [number, number, number];
                return axes.every((a, i) => Math.abs(a - defAxes[i]) < 0.1);
            });
            if (isAllDefault && prevHistoryLength.current !== useDanceStore.getState().history.length) {
                advanceStep();
            }
        }

        // Detect addKeyframe action
        if (currentStep.actionRequired === 'addKeyframe' && keyframes.length > prevKeyframesLength.current) {
            advanceStep();
        }

        // Detect loadMusic action
        if (currentStep.actionRequired === 'loadMusic' && hasMusicLoaded && !prevHasMusicLoaded.current) {
            advanceStep();
        }

        // Detect changeBeatSubdivision action
        if (currentStep.actionRequired === 'changeBeatSubdivision' && beatSubdivision !== prevBeatSubdivision.current) {
            advanceStep();
        }

        // Detect changeVolume action
        if (currentStep.actionRequired === 'changeVolume' && musicVolume !== prevMusicVolume.current) {
            advanceStep();
        }

        // Detect removeMusic action
        if (currentStep.actionRequired === 'removeMusic' && !hasMusicLoaded && prevHasMusicLoaded.current) {
            advanceStep();
        }

        // Detect switchTab action
        if (currentStep.actionRequired === 'switchTab') {
            if (activeModule === currentStep.targetValue) {
                advanceStep();
            }
        }

        // Detect loadPose action (double click)
        if (currentStep.actionRequired === 'loadPose') {
            if (actingSource && actingSource.type === 'library') {
                advanceStep();
            }
        }

        // Detect createFolder action
        if (currentStep.actionRequired === 'createFolder' && folders.length > prevFoldersLength.current) {
            advanceStep();
        }

        // Detect savePoseToLibrary action
        if (currentStep.actionRequired === 'savePoseToLibrary' && poses.length > prevPosesLength.current) {
            advanceStep();
        }

        // Detect selectJoint action
        if (currentStep.actionRequired === 'selectJoint' && selectedJoint === currentStep.targetValue) {
            advanceStep();
        }

        // Detect selectFrame action
        if (currentStep.actionRequired === 'selectFrame' && Math.abs(currentTime - parseFloat(currentStep.targetValue || '0')) < 0.05) {
            advanceStep();
        }

        // Detect rotateJoint action
        if (currentStep.actionRequired === 'rotateJoint') {
            const parts = currentStep.targetId.split('-');
            const jointName = parts[2];
            const axis = parts[3];
            const axisIndex = axis === 'X' ? 0 : axis === 'Y' ? 1 : 2;
            const currentVal = ((currentPose[jointName] as JointRotation) || [0, 0, 0])[axisIndex];
            if (Math.abs(currentVal - parseFloat(currentStep.targetValue || '0')) < 5) {
                advanceStep();
            }
        }

        // Detect rotateJointMouse action
        if (currentStep.actionRequired === 'rotateJointMouse') {
            let jointName = currentStep.targetValue;
            if (currentStep.id === 'adv-drag-elbow-drag') {
                jointName = 'leftShoulder';
            }
            if (jointName && currentPose[jointName] && initialJointPoseRef.current) {
                const axes = currentPose[jointName] as [number, number, number];
                const init = initialJointPoseRef.current;
                const hasMovedEnough = axes.some((val, i) => Math.abs(val - init[i]) > 2.0);

                if (hasMovedEnough && isPosing) {
                    movementDetectedThisStepRef.current = true;
                }

                if (movementDetectedThisStepRef.current && !isPosing) {
                    movementDetectedThisStepRef.current = false;
                    advanceStep();
                }
            }
        }

        // Detect conical rotation (A key)
        if (currentStep.actionRequired === 'rotateConicalMouse') {
            const jointName = currentStep.targetValue;
            if (jointName && currentPose[jointName] && initialJointPoseRef.current) {
                const axes = currentPose[jointName] as [number, number, number];
                const init = initialJointPoseRef.current;
                const hasRotatedConically = Math.abs(axes[0] - init[0]) > 2.0 || Math.abs(axes[2] - init[2]) > 2.0;

                if (hasRotatedConically && isPosing) {
                    movementDetectedThisStepRef.current = true;
                }

                if (movementDetectedThisStepRef.current && !isPosing) {
                    movementDetectedThisStepRef.current = false;
                    advanceStep();
                }
            }
        }

        // Detect torso rotation (F key)
        if (currentStep.actionRequired === 'rotateTorsoMouse') {
            const jointName = currentStep.targetValue;
            if (jointName && currentPose[jointName] && initialJointPoseRef.current) {
                const axes = currentPose[jointName] as [number, number, number];
                const init = initialJointPoseRef.current;
                const hasRotatedAroundTorso = activeAxis === 'F' && (Math.abs(axes[0] - init[0]) > 2.0 || Math.abs(axes[2] - init[2]) > 2.0);

                if (hasRotatedAroundTorso && isPosing) {
                    movementDetectedThisStepRef.current = true;
                }

                if (movementDetectedThisStepRef.current && !isPosing) {
                    movementDetectedThisStepRef.current = false;
                    advanceStep();
                }
            }
        }

        // Detect floor movement (D key)
        if (currentStep.actionRequired === 'moveFloorMouse') {
            if (currentPose.elevation && initialMannequinPosRef.current) {
                const pos = currentPose.elevation;
                const init = initialMannequinPosRef.current;
                const hasMovedOnFloor = Math.abs(pos[0] - init[0]) > 0.05 || Math.abs(pos[2] - init[2]) > 0.05;

                if (hasMovedOnFloor && isPosing) {
                    movementDetectedThisStepRef.current = true;
                }

                if (movementDetectedThisStepRef.current && !isPosing) {
                    movementDetectedThisStepRef.current = false;
                    advanceStep();
                }
            }
        }

        // Detect elevation change (Slider or S key)
        if (currentStep.actionRequired === 'changeElevation') {
            if (currentPose.elevation && initialMannequinPosRef.current) {
                const currentY = currentPose.elevation[1];
                const initY = initialMannequinPosRef.current[1];
                if (Math.abs(currentY - initY) > 0.05 && isPosing) {
                    movementDetectedThisStepRef.current = true;
                }

                if (movementDetectedThisStepRef.current && !isPosing) {
                    movementDetectedThisStepRef.current = false;
                    advanceStep();
                }
            }
        }

        // Detect grounding action
        if (currentStep.actionRequired === 'groundMannequin') {
            const groundElev = calculateGroundedElevation(currentPose);
            const currentElevY = (currentPose.elevation || [0, 0, 0])[1];
            if (Math.abs(currentElevY - groundElev) < 0.01) {
                advanceStep();
            }
        }

        // Detect resetJoint action
        if (currentStep.actionRequired === 'resetJoint') {
            const jointName = currentStep.targetValue;
            if (jointName && currentPose[jointName]) {
                const axes = currentPose[jointName] as [number, number, number];
                const defaultVal = (defaultPose as any)[jointName] || [0, 0, 0];
                // Check if all axes are balanced at their default values
                const isReset = axes.every((ax, idx) => Math.abs(ax - defaultVal[idx]) < 0.01);
                if (isReset) {
                    advanceStep();
                }
            }
        }

        // Detect playSequence action
        if (currentStep.actionRequired === 'playSequence' && isPlaying && !prevIsPlaying.current) {
            advanceStep();
        }

        // Detect stopSequence action
        if (currentStep.actionRequired === 'stopSequence' && !isPlaying && prevIsPlaying.current) {
            advanceStep();
        }

        // Detect saveSequence action
        if (currentStep.actionRequired === 'saveSequence') {
            const hasStartedSave = loadedSequence?.id !== prevLoadedSequenceId.current;
            if ((loadedSequence && hasStartedSave) || (loadedSequence && !hasUnsavedSequenceChanges)) {
                advanceStep();
            }
        }

        // Detect addFootMarker action
        if (currentStep.actionRequired === 'addFootMarker' && footMarkers.length > prevFootMarkersLength.current) {
            advanceStep();
        }

        // Detect addLocationMarker action
        if (currentStep.actionRequired === 'addLocationMarker' && locationMarkers.length > prevLocationMarkersLength.current) {
            advanceStep();
        }

        // Detect clearAllMarkers action
        if (currentStep.actionRequired === 'clearAllMarkers' &&
            footMarkers.length === 0 && locationMarkers.length === 0 &&
            (prevFootMarkersLength.current > 0 || prevLocationMarkersLength.current > 0)) {
            advanceStep();
        }

        // Detect snapToMarker action
        if (currentStep.actionRequired === 'snapToMarker') {
            const hasSnapped = [...footMarkers, ...locationMarkers].some(m =>
                Math.abs((currentPose.elevation?.[0] || 0) - m.position[0]) < 0.05 &&
                Math.abs((currentPose.elevation?.[2] || 0) - m.position[2]) < 0.05
            );
            if (hasSnapped && prevHistoryLength.current !== useDanceStore.getState().history.length) {
                advanceStep();
            }
        }

        prevKeyframesLength.current = keyframes.length;
        prevIsPlaying.current = isPlaying;
        prevLoadedSequenceId.current = loadedSequence?.id;
        prevIsMirroring.current = isMirroring;
        prevShowLabels.current = showLabels;
        prevShowAxes.current = showAxes;
        prevFootMarkersLength.current = footMarkers.length;
        prevLocationMarkersLength.current = locationMarkers.length;
        prevHistoryLength.current = useDanceStore.getState().history.length;

        prevDuration.current = duration;
        prevTimelineZoom.current = timelineZoom;
        prevIsTimelineCompact.current = isTimelineCompact;
        prevMetronomeInterval.current = metronomeInterval;
        prevCopiedFramesLength.current = copiedFrames.length;
        prevSelectedFrameTimesLength.current = selectedFrameTimes.length;
        prevFrameMarkersCount.current = getFrameMarkersCount(frameMarkers);

        prevHasMusicLoaded.current = hasMusicLoaded;
        prevBeatSubdivision.current = beatSubdivision;
        prevMusicVolume.current = musicVolume;

        prevFoldersLength.current = folders.length;
        prevPosesLength.current = poses.length;
        prevActiveModule.current = activeModule;
    }, [keyframes.length, keyframes, history.length, selectedJoint, currentTime, currentPose, isPosing, isPlaying, hasUnsavedSequenceChanges, loadedSequence, currentStep, tutorialProgress, updateTutorialProgress, currentModule, isMirroring, showLabels, showAxes, footMarkers.length, locationMarkers.length, duration, timelineZoom, isTimelineCompact, metronomeInterval, copiedFrames.length, selectedFrameTimes.length, frameMarkers, hasMusicLoaded, beatSubdivision, musicVolume, folders.length, poses.length, activeModule, actingSource]);

    // Global click listener to finish tutorial on "none" action steps (like Conclusion)
    useEffect(() => {
        if (!isVisible || !currentStep || currentStep.actionRequired !== 'none') return;

        const handleGlobalClick = () => {
            // If we are on a step with no required action (Conclusion), any click finishes/closes the tutorial
            updateTutorialProgress({ currentModuleId: null, currentStepIndex: 0 });
            setDuration(10);
        };

        // Delay slightly to prevent the click that might have triggered this step from immediately closing it
        const timer = setTimeout(() => {
            window.addEventListener('click', handleGlobalClick);
        }, 300);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('click', handleGlobalClick);
        };
    }, [isVisible, currentStep, updateTutorialProgress, setDuration]);

    // Generic click detection for 'click' action required
    useEffect(() => {
        if (!isVisible || !currentStep || currentStep.actionRequired !== 'click') return;

        const handleElementClick = (e: MouseEvent) => {
            const target = document.getElementById(currentStep.targetId);
            if (target && target.contains(e.target as Node)) {
                advanceStep();
            }
        };

        window.addEventListener('click', handleElementClick);
        return () => window.removeEventListener('click', handleElementClick);
    }, [isVisible, currentStep, currentStep?.targetId]);

    // Polling check for DOM-based tutorial actions (since their triggering state is local to other components)
    useEffect(() => {
        if (!isVisible || !currentStep) return;
        const actions = ['openFrameNoteInput', 'saveFrameNote', 'hoverFrame'];
        if (!actions.includes(currentStep.actionRequired)) return;

        const interval = setInterval(() => {
            if (currentStep.actionRequired === 'openFrameNoteInput') {
                const textarea = document.getElementById('keyframe-note-textarea');
                if (textarea) {
                    advanceStep();
                }
            } else if (currentStep.actionRequired === 'saveFrameNote') {
                const kf = keyframes.find(k => Math.abs(k.time - 4.0) < 0.05);
                if (kf?.note && kf.note.trim().length > 0 && !document.getElementById('keyframe-note-textarea')) {
                    advanceStep();
                }
            } else if (currentStep.actionRequired === 'hoverFrame') {
                const tooltip = document.querySelector('.pose-preview-popup');
                if (tooltip) {
                    advanceStep();
                }
            }
        }, 100);

        return () => clearInterval(interval);
    }, [isVisible, currentStep, keyframes]);

    // Update target position
    useEffect(() => {
        if (!currentStep) {
            setTargetRects([]);
            setTargetRect(null);
            setIsVisible(false);
            return;
        }

        const updatePosition = () => {
            if (!currentStep.targetId && !currentStep.supportingTargetId) {
                setTargetRects([]);
                setSupportingRects([]);
                setTargetRect(null);
                setIsVisible(true);
                setIsPositionStable(true);
                return;
            }

            // Primary Action Targets
            const ids = currentStep.targetId?.split(',') || [];
            const rects: DOMRect[] = [];
            if (currentStep.id === 'seq-navigation' || currentStep.id === 'seq-zoom' || currentStep.id === 'seq-add-dot' || currentStep.id === 'wm-undo-redo' || currentStep.id === 'adv-torso-intro') {
                const buttons = ids.map(id => document.getElementById(id.trim())).filter(el => !!el);
                if (buttons.length > 0) {
                    const buttonRects = buttons.map(b => b!.getBoundingClientRect());
                    const minLeft = Math.min(...buttonRects.map(r => r.left));
                    const maxRight = Math.max(...buttonRects.map(r => r.right));
                    const minTop = Math.min(...buttonRects.map(r => r.top));
                    const maxBottom = Math.max(...buttonRects.map(r => r.bottom));
                    const combinedRect = {
                        left: minLeft,
                        top: minTop,
                        right: maxRight,
                        bottom: maxBottom,
                        width: maxRight - minLeft,
                        height: maxBottom - minTop,
                        x: minLeft,
                        y: minTop,
                        toJSON: () => { }
                    } as DOMRect;
                    rects.push(combinedRect);
                }
            } else if (currentStep.id === 'shortcuts-stage') {
                const menuEl = document.querySelector('.context-menu');
                if (menuEl) {
                    rects.push(menuEl.getBoundingClientRect());
                } else {
                    const el = document.getElementById('stage-3d-container');
                    if (el) {
                        const r = el.getBoundingClientRect();
                        const cx = r.left + r.width / 2;
                        const cy = r.top + r.height / 2;
                        rects.push({
                            left: cx - 2,
                            top: cy - 2,
                            right: cx + 2,
                            bottom: cy + 2,
                            width: 4,
                            height: 4,
                            x: cx - 2,
                            y: cy - 2,
                            toJSON: () => { }
                        } as DOMRect);
                    }
                }
            } else {
                ids.forEach(id => {
                    const el = document.getElementById(id.trim());
                    if (el) rects.push(el.getBoundingClientRect());
                });
            }

            // Supporting Clue Targets
            const sIds = currentStep.supportingTargetId?.split(',') || [];
            const sRects: DOMRect[] = [];
            sIds.forEach(id => {
                const el = document.getElementById(id.trim());
                if (el) sRects.push(el.getBoundingClientRect());
            });

            setTargetRects(rects);
            setSupportingRects(sRects);

            let foundTarget = false;
            if (rects.length > 0) {
                setTargetRect(rects[0]);
                foundTarget = true;
            } else if (sRects.length > 0) {
                setTargetRect(sRects[0]);
                foundTarget = true;
            } else {
                setTargetRect(null);
            }

            // Only mark as stable if we found the target we were looking for, or if no target was needed
            const needsTarget = !!(currentStep.targetId || currentStep.supportingTargetId);
            if (!needsTarget || foundTarget) {
                setIsPositionStable(true);
            }

            setIsVisible(true);
        };

        updatePosition();

        window.addEventListener('resize', updatePosition);
        const interval = setInterval(updatePosition, 50);

        return () => {
            window.removeEventListener('resize', updatePosition);
            clearInterval(interval);
        };
    }, [currentStep]);

    // Simulate right-click context menu when showcased steps become stable
    useEffect(() => {
        if (!currentStep || !isPositionStable) return;
        if (simulatedStepsRef.current === currentStep.id) return;
        simulatedStepsRef.current = currentStep.id;

        if (currentModule?.id === 'context-shortcuts') {
            useDanceStore.getState().closeContextMenu();

            // Slight delay so DOM/Spotlight is completely initialized
            setTimeout(() => {
                if (currentStep.id === 'shortcuts-stage') {
                    const el = document.getElementById('stage-3d-container');
                    if (el) {
                        const rect = el.getBoundingClientRect();
                        // Position 200px right and 100px up from center to avoid mannequin and prevent crowding the tutorial window
                        const x = rect.left + rect.width / 2 + 200;
                        const y = rect.top + rect.height / 2 - 100;
                        const evt = new MouseEvent('contextmenu', {
                            bubbles: true,
                            cancelable: true,
                            clientX: x,
                            clientY: y
                        });
                        el.dispatchEvent(evt);
                    }
                } else if (currentStep.id === 'shortcuts-joint') {
                    const el = document.getElementById('tutorial-3d-target');
                    if (el) {
                        const rect = el.getBoundingClientRect();
                        const x = rect.left + rect.width / 2;
                        const y = rect.top + rect.height / 2;
                        const resetJoint = useDanceStore.getState().resetJoint;
                        const copyJointToOpposite = useDanceStore.getState().copyJointToOpposite;
                        const copyJointFromOpposite = useDanceStore.getState().copyJointFromOpposite;
                        const menuItems = [
                            { label: t('sidebar.reset_joint'), icon: RotateCcw, action: () => resetJoint('leftShoulder') },
                            { isDivider: true },
                            { label: t('sidebar.copy_to_opposite'), icon: Copy, action: () => copyJointToOpposite('leftShoulder') },
                            { label: t('sidebar.copy_from_opposite'), icon: ArrowLeftRight, action: () => copyJointFromOpposite('leftShoulder') }
                        ];
                        useDanceStore.getState().openContextMenu(x, y, menuItems);
                    }
                } else if (currentStep.id === 'shortcuts-timeline') {
                    const el = document.getElementById('timeline-frame-0.000');
                    if (el) {
                        const rect = el.getBoundingClientRect();
                        const x = rect.left + rect.width / 2;
                        const y = rect.top + rect.height / 2;
                        const evt = new MouseEvent('contextmenu', {
                            bubbles: true,
                            cancelable: true,
                            clientX: x,
                            clientY: y
                        });
                        el.dispatchEvent(evt);
                    }
                } else if (currentStep.id === 'notes-create-frame-note') {
                    const el = document.getElementById('timeline-frame-4.000');
                    if (el) {
                        const rect = el.getBoundingClientRect();
                        const x = rect.left + rect.width / 2;
                        const y = rect.top + rect.height / 2;
                        const evt = new MouseEvent('contextmenu', {
                            bubbles: true,
                            cancelable: true,
                            clientX: x,
                            clientY: y
                        });
                        el.dispatchEvent(evt);
                    }
                } else if (currentStep.id === 'shortcuts-folders') {
                    const el = document.getElementById('library-folder-default-poses');
                    if (el) {
                        const rect = el.getBoundingClientRect();
                        const x = rect.left + rect.width / 2;
                        const y = rect.top + rect.height / 2;
                        const evt = new MouseEvent('contextmenu', {
                            bubbles: true,
                            cancelable: true,
                            clientX: x,
                            clientY: y
                        });
                        el.dispatchEvent(evt);
                    }
                } else if (currentStep.id === 'shortcuts-library') {
                    const el = document.getElementById('library-first-pose-card');
                    if (el) {
                        const rect = el.getBoundingClientRect();
                        const x = rect.left + rect.width / 2;
                        const y = rect.top + rect.height / 2;
                        const evt = new MouseEvent('contextmenu', {
                            bubbles: true,
                            cancelable: true,
                            clientX: x,
                            clientY: y
                        });
                        el.dispatchEvent(evt);
                    }
                }
            }, 100);
        }
    }, [currentStep?.id, isPositionStable, currentModule?.id, t]);

    // Safety check for targetId to avoid .includes on null/undefined
    const targetId = currentStep?.targetId || '';
    const is3DTarget = targetId.includes('tutorial-target') || targetId.includes('tutorial-3d-target');

    // Calculate tooltip position
    let tooltipWidth = displayedStep?.showVisual === 'scrollRotation' ? 280 : (is3DTarget ? 180 : (displayedStep?.showVisual === 'viewControls' ? 250 : 260));
    const gap = is3DTarget ? 115 : 135;

    let top = 0;
    let left = 0;
    let placement: 'top' | 'bottom' | 'left' | 'right' = 'top';

    if (!targetRect) {
        // Positioned between the Core sidebar and the Mannequin to avoid obscuring the center
        top = window.innerHeight - tooltipHeight - 240;
        left = 320;
        placement = 'bottom';

        if (currentStep?.id === 'snap-to-marker') {
            left = 550; // Place it to the right of the location marker to avoid covering it
        }

        if (currentStep?.id === 'quick-edit-intro' || currentStep?.id === 'wm-intro') {
            top -= window.innerHeight * 0.25;
            left -= window.innerWidth * 0.05;
        }

        if (currentStep?.id === 'seq-music-intro') {
            top -= 50;
        }
    } else if (is3DTarget) {
        // Favor the right side (between mannequin and Pose Studio) for centered 3D targets
        const isRightHalf = targetRect.left > (window.innerWidth / 2) - 80;
        if (isRightHalf) {
            placement = 'right';
            left = Math.min(window.innerWidth - tooltipWidth - 30, targetRect.right + gap);
        } else {
            placement = 'left';
            left = Math.max(30, targetRect.left - tooltipWidth - gap);
        }
        top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2);
    } else if (targetRect) {
        top = targetRect.top - tooltipHeight - gap;
        left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
        placement = 'top';
        left = Math.max(20, Math.min(left, window.innerWidth - tooltipWidth - 20));
        if (top < 40) {
            top = targetRect.bottom + gap;
            placement = 'bottom';
        }
    } else {
        // Fallback for when currentStep has a targetId but the element isn't in DOM
        top = window.innerHeight - tooltipHeight - 240;
        left = 320;
        placement = 'bottom';
    }

    const isTorsoStep = currentStep?.id === 'adv-torso-intro' ||
        currentStep?.id === 'adv-torso-t' ||
        currentStep?.id === 'adv-torso-v' ||
        currentStep?.id === 'adv-torso-l' ||
        currentStep?.id === 'adv-drag-relationship-intro' ||
        currentStep?.id === 'adv-drag-elbow-drag' ||
        currentStep?.id === 'adv-torso-conclusion';

    if (isTorsoStep) {
        const timelineEl = document.querySelector('.timeline-panel');
        const timelineRect = timelineEl?.getBoundingClientRect();
        const timelineTop = timelineRect ? timelineRect.top : (window.innerHeight - 240);
        top = timelineTop - tooltipHeight - 20;
        left = 270;
        placement = 'right';
    }

    top = Math.max(20, Math.min(top, window.innerHeight - tooltipHeight - 20));
    left = Math.max(20, Math.min(left, window.innerWidth - tooltipWidth - 20));

    const isFixedStep = currentStep?.id === 'view-controls' ||
        currentStep?.id === 'vertical-attitude-rotate' ||
        currentStep?.id === 'vertical-attitude-move' ||
        currentStep?.id === 'vertical-attitude-conical' ||
        currentStep?.id === 'torso-rotation' ||
        currentStep?.id === 'snap-to-marker' ||
        currentStep?.id === 'stage-orientations' ||
        currentStep?.id === 'shortcuts-stage' ||
        currentStep?.id === 'shortcuts-joint' ||
        currentStep?.id === 'shortcuts-timeline' ||
        currentStep?.id === 'shortcuts-folders' ||
        currentStep?.id === 'shortcuts-library' ||
        currentStep?.id === 'seq-navigation' ||
        currentStep?.id === 'seq-zoom' ||
        currentStep?.id === 'seq-change-duration' ||
        currentStep?.id === 'seq-change-interval' ||
        currentStep?.id === 'seq-move-multi' ||
        currentStep?.id === 'seq-clear-timeline' ||
        currentStep?.id === 'wm-undo-redo' ||
        currentStep?.id === 'wm-confirm-reset' ||
        currentStep?.id === 'wm-import-export' ||
        currentStep?.id === 'wm-random-pose' ||
        currentStep?.id === 'wm-randomness' ||
        currentStep?.id === 'lib-create-folder' ||
        currentStep?.id === 'seq-music-waveform' ||
        currentStep?.id === 'seq-music-grid' ||
        currentStep?.id === 'seq-music-subdivision-effect' ||
        currentStep?.id === 'seq-music-dot' ||
        currentStep?.id === 'notes-create-frame-note' ||
        currentStep?.id === 'notes-write-frame-note' ||
        currentStep?.id === 'seq-music-options';

    useEffect(() => {
        if (isFixedStep && !frozenTooltipPos && (targetRect || currentStep.id === 'view-controls') && isVisible) {
            const rect = targetRect as DOMRect;
            let finalTop = top;
            let finalLeft = left;
            let finalPlacement = placement;

            // Enforce distinct positions for specialized steps to avoid "jumping" and overlap with context menus
            if (currentStep.id === 'view-controls') {
                finalTop = 150;
                finalLeft = 200;
                finalPlacement = 'right';
            } else if (currentStep.id === 'stage-orientations') {
                finalTop = 100;
                finalLeft = 100;
                finalPlacement = 'right';
            } else if (currentStep.id === 'vertical-attitude-rotate') {
                // Step 5: High Right
                finalPlacement = 'right';
                finalLeft = rect.right + gap + 40;
                finalTop = rect.top - 40;
            } else if (currentStep.id === 'vertical-attitude-move') {
                // Step 6: Low Left
                finalPlacement = 'left';
                finalLeft = rect.left - tooltipWidth - gap - 40;
                finalTop = rect.top + 80;
            } else if (currentStep.id === 'vertical-attitude-conical' || currentStep.id === 'torso-rotation') {
                // Position the tutorial box in between the left sidebars and the mannequin
                finalPlacement = 'left';
                finalLeft = 320;
                finalTop = window.innerHeight - tooltipHeight - 240;
            } else if (currentStep.id === 'snap-to-marker') {
                // Position to the right of the target (the snap button popup) to avoid covering the marker or the popup
                finalPlacement = 'right';
                finalLeft = rect.right + 40;
                finalTop = rect.top - 20;
            } else if (currentStep.id === 'shortcuts-stage') {
                // Position the Stage context menu tutorial window near the top-left of the Stage
                // to completely avoid the center-triggered context menu
                finalTop = 120;
                finalLeft = 320; // just to the right of the core sidebar
                finalPlacement = 'right';
            } else if (currentStep.id === 'shortcuts-joint') {
                // Position the Joint tooltip on the LEFT of the joint (which is in the center-left)
                // giving 160px gap so it doesn't overlap the right-click menu extending to the right
                finalPlacement = 'left';
                finalLeft = Math.max(30, rect.left - tooltipWidth - 160);
                finalTop = rect.top - 40;
            } else if (currentStep.id === 'shortcuts-timeline') {
                // Position the Timeline tooltip to the RIGHT and ABOVE the timeline tick
                // to avoid overlapping the flipped context menu rising up from the bottom
                finalPlacement = 'top';
                finalLeft = rect.right + 120;
                finalTop = rect.top - tooltipHeight - 80;
            } else if (currentStep.id === 'notes-create-frame-note') {
                // Position the tooltip to the left of Frame 5 (timeline-frame-4.000)
                // so its right border is away from the left border of the frame.
                // This ensures the context menu does not compete with the instruction window.
                const frameEl = document.getElementById('timeline-frame-4.000');
                if (frameEl) {
                    const frameRect = frameEl.getBoundingClientRect();
                    finalPlacement = 'left';
                    finalLeft = frameRect.left - tooltipWidth - 80; // 80px gap from the frame's left edge
                    finalTop = frameRect.top - (tooltipHeight / 2) + (frameRect.height / 2);
                }
            } else if (currentStep.id === 'notes-write-frame-note') {
                // Position the tooltip to the right of the note edit popup
                // so the arrow pointing to the checkmark button does not cross over the text area.
                const popupEl = document.querySelector('.pose-preview-popup');
                if (popupEl) {
                    const popupRect = popupEl.getBoundingClientRect();
                    finalPlacement = 'right';
                    finalLeft = popupRect.right + 40; // 40px gap from the popup's right edge
                    finalTop = popupRect.top + (popupRect.height / 2) - (tooltipHeight / 2);
                } else {
                    const frameEl = document.getElementById('timeline-frame-4.000');
                    if (frameEl) {
                        const frameRect = frameEl.getBoundingClientRect();
                        finalPlacement = 'right';
                        finalLeft = frameRect.right + 260; // fallback gap to approximate right-placement
                        finalTop = frameRect.top - (tooltipHeight / 2) + (frameRect.height / 2);
                    }
                }
            } else if (currentStep.id === 'shortcuts-folders') {
                // Position the Folders tree tooltip on the RIGHT of the folder element
                // with 160px gap to avoid collision with its context menu expanding rightward
                finalPlacement = 'right';
                finalLeft = rect.right + 160;
                finalTop = rect.top - 20;
            } else if (currentStep.id === 'shortcuts-library') {
                // Position the Library card tooltip on the RIGHT of the card/menu
                // with 240px gap to cleanly clear the context menu extending to the right
                finalPlacement = 'right';
                finalLeft = rect.right + 240;
                finalTop = rect.top + 20;
            } else if (currentStep.id === 'seq-navigation') {
                // Position the Speed Navigation tutorial window in between the mannequin and the Core/Attitude side bars
                finalPlacement = 'top';
                finalLeft = 320;
                finalTop = window.innerHeight - tooltipHeight - 240;
            } else if (currentStep.id === 'seq-zoom') {
                // Position the Zoom tutorial window higher up to give the zoom controls breathing room
                finalPlacement = 'top';
                finalLeft = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                finalTop = rect.top - tooltipHeight - 190;
            } else if (currentStep.id === 'seq-change-duration' || currentStep.id === 'seq-change-interval') {
                // Position the duration/interval settings tutorial window higher up to give controls breathing room
                finalPlacement = 'top';
                finalLeft = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                finalTop = rect.top - tooltipHeight - 195;
            } else if (currentStep.id === 'wm-undo-redo') {
                // Position the undo/redo tutorial window about 15% left and 10% up to prevent overlap
                finalPlacement = 'top';
                finalLeft = rect.left + (rect.width / 2) - (tooltipWidth / 2) - window.innerWidth * 0.15;
                finalTop = rect.top - tooltipHeight - 35 - window.innerHeight * 0.10;
            } else if (currentStep.id === 'wm-confirm-reset') {
                // Position the confirm reset safety lock tutorial window to the right of the sidebar, between the sidebar and the mannequin
                finalPlacement = 'right';
                finalLeft = rect.right + 280;
                finalTop = rect.top - 60;
            } else if (currentStep.id === 'wm-import-export') {
                // Move the import/export tutorial window to the right of the sidebar so it does not overlap the Main Menu
                finalPlacement = 'right';
                finalLeft = rect.right + 120;
                finalTop = rect.top - tooltipHeight - 150;
            } else if (currentStep.id === 'seq-move-multi') {
                // Position at the top-center of the screen to completely avoid overlapping the timeline at the bottom
                finalPlacement = 'bottom';
                finalLeft = (window.innerWidth / 2) - (tooltipWidth / 2);
                finalTop = 80;
            } else if (currentStep.id === 'seq-clear-timeline') {
                // Position closely above the Clear Timeline button, shifted 100px down as requested
                finalPlacement = 'top';
                finalLeft = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                finalTop = rect.top - tooltipHeight + 76;
            } else if (currentStep.id === 'wm-random-pose' || currentStep.id === 'wm-randomness') {
                // Position between the mannequin and the left sidebar to prevent overlaying the mannequin
                finalPlacement = 'top';
                finalLeft = 320;
                finalTop = window.innerHeight - tooltipHeight - 240;
            } else if (currentStep.id === 'lib-create-folder') {
                // Position the tutorial box in the middle of the screen for Library Management Step 3
                finalPlacement = 'bottom';
                finalLeft = (window.innerWidth / 2) - (tooltipWidth / 2);
                finalTop = (window.innerHeight / 2) - (tooltipHeight / 2);
            } else if (currentStep.id === 'seq-music-options') {
                // Position the tutorial box to the right of the centered 480px soundtrack modal
                finalPlacement = 'left';
                finalLeft = (window.innerWidth / 2) + 260;
                finalTop = (window.innerHeight / 2) - (tooltipHeight / 2);
            } else if (
                currentStep.id === 'seq-music-waveform' ||
                currentStep.id === 'seq-music-grid' ||
                currentStep.id === 'seq-music-dot' ||
                currentStep.id === 'seq-music-subdivision-effect'
            ) {
                // Position the tutorial box in between the left sidebars and the mannequin
                finalPlacement = 'top';
                finalLeft = 320;
                finalTop = window.innerHeight - tooltipHeight - 240;
            }

            // Clamp final values to screen
            finalTop = Math.max(20, Math.min(finalTop, window.innerHeight - tooltipHeight - 20));
            finalLeft = Math.max(20, Math.min(finalLeft, window.innerWidth - tooltipWidth - 20));

            setFrozenTooltipPos({ top: finalTop, left: finalLeft, placement: finalPlacement });
        }
    }, [isFixedStep, frozenTooltipPos, targetRect, isVisible, top, left, placement, currentStep?.id, tooltipWidth, gap, tooltipHeight]);

    const isAnyModalOpen = (confirmDialog && confirmDialog.isOpen) ||
        (inputDialog && inputDialog.isOpen) ||
        isSettingsOpen ||
        isFeedbackOpen;

    if (!currentStep || !isVisible || isAnyModalOpen) return null;

    const rawFinalTop = (isFixedStep && frozenTooltipPos) ? frozenTooltipPos.top : top;
    const rawFinalLeft = (isFixedStep && frozenTooltipPos) ? frozenTooltipPos.left : left;
    const rawFinalPlacement = (isFixedStep && frozenTooltipPos) ? frozenTooltipPos.placement : placement;

    const finalTop = currentStep?.id === 'lib-create-folder' ? (window.innerHeight / 2) - (tooltipHeight / 2) : rawFinalTop;
    const finalLeft = currentStep?.id === 'lib-create-folder' ? (window.innerWidth / 2) - (tooltipWidth / 2) : rawFinalLeft;
    const finalPlacement = currentStep?.id === 'lib-create-folder' ? 'bottom' : rawFinalPlacement;

    // Use final positions for line calculation
    top = finalTop;
    left = finalLeft;
    placement = finalPlacement;

    // Real-time overlap/collision detection and resolution with Context Menu
    const contextMenuState = useDanceStore.getState().contextMenu;
    if (contextMenuState && contextMenuState.isOpen) {
        const menuEl = document.querySelector('.context-menu');
        let menuBox = { left: contextMenuState.x, top: contextMenuState.y, right: contextMenuState.x + 200, bottom: contextMenuState.y + 220 };

        if (menuEl) {
            const r = menuEl.getBoundingClientRect();
            menuBox = { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
        }

        const menuWidth = menuBox.right - menuBox.left;
        const menuHeight = menuBox.bottom - menuBox.top;

        // Check if tooltip overlaps with context menu
        const tooltipBox = { left, top, right: left + tooltipWidth, bottom: top + tooltipHeight };
        const overlapX = Math.max(0, Math.min(tooltipBox.right, menuBox.right) - Math.max(tooltipBox.left, menuBox.left));
        const overlapY = Math.max(0, Math.min(tooltipBox.bottom, menuBox.bottom) - Math.max(tooltipBox.top, menuBox.top));

        if (overlapX > 0 && overlapY > 0) {
            // They overlap! Resolve collision.
            const safetyPadding = 32; // Premium visual distance

            const tooltipCenterX = left + tooltipWidth / 2;
            const tooltipCenterY = top + tooltipHeight / 2;
            const menuCenterX = menuBox.left + menuWidth / 2;
            const menuCenterY = menuBox.top + menuHeight / 2;

            const diffX = tooltipCenterX - menuCenterX;
            const diffY = tooltipCenterY - menuCenterY;

            // Try shifting horizontally first if we are further to the side, or vertically otherwise
            let resolved = false;

            if (Math.abs(diffX) >= Math.abs(diffY)) {
                // Try horizontal shift
                let shiftLeft = diffX > 0 ? (menuBox.right + safetyPadding) : (menuBox.left - tooltipWidth - safetyPadding);
                // Clamp to viewport
                shiftLeft = Math.max(20, Math.min(shiftLeft, window.innerWidth - tooltipWidth - 20));

                // Verify if it still overlaps after clamping
                const newOverlapX = Math.max(0, Math.min(shiftLeft + tooltipWidth, menuBox.right) - Math.max(shiftLeft, menuBox.left));
                if (newOverlapX === 0) {
                    left = shiftLeft;
                    resolved = true;
                }
            }

            if (!resolved) {
                // Try vertical shift
                let shiftTop = diffY > 0 ? (menuBox.bottom + safetyPadding) : (menuBox.top - tooltipHeight - safetyPadding);
                // Clamp to viewport
                shiftTop = Math.max(20, Math.min(shiftTop, window.innerHeight - tooltipHeight - 20));

                // Verify if it still overlaps
                const newOverlapY = Math.max(0, Math.min(shiftTop + tooltipHeight, menuBox.bottom) - Math.max(shiftTop, menuBox.top));
                if (newOverlapY === 0) {
                    top = shiftTop;
                    resolved = true;
                }
            }

            if (!resolved) {
                // If neither worked due to tight screen constraints, force a shift in the direction of the side
                if (Math.abs(diffX) >= Math.abs(diffY)) {
                    left = diffX > 0 ? (menuBox.right + safetyPadding) : (menuBox.left - tooltipWidth - safetyPadding);
                } else {
                    top = diffY > 0 ? (menuBox.bottom + safetyPadding) : (menuBox.top - tooltipHeight - safetyPadding);
                }
                // Clamp anyway
                left = Math.max(20, Math.min(left, window.innerWidth - tooltipWidth - 20));
                top = Math.max(20, Math.min(top, window.innerHeight - tooltipHeight - 20));
            }
        }
    }

    // Helper to calculate line coordinates
    const calculateLines = (rects: DOMRect[]) => {
        const tooltipEl = tooltipRef.current;
        const tooltipBox = tooltipEl ? tooltipEl.getBoundingClientRect() : null;

        return rects.map(rect => {
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;

            let lx1 = 0, ly1 = 0;
            const boxWidth = tooltipBox ? tooltipBox.width : tooltipWidth;
            const boxHeight = tooltipBox ? tooltipBox.height : tooltipHeight;
            const boxLeft = tooltipBox ? tooltipBox.left : left;
            const boxTop = tooltipBox ? tooltipBox.top : top;

            switch (placement) {
                case 'top': lx1 = boxLeft + boxWidth / 2; ly1 = boxTop + boxHeight; break;
                case 'bottom': lx1 = boxLeft + boxWidth / 2; ly1 = boxTop; break;
                case 'left': lx1 = boxLeft + boxWidth; ly1 = boxTop + boxHeight / 2; break;
                case 'right': lx1 = boxLeft; ly1 = boxTop + boxHeight / 2; break;
            }

            if (currentStep?.id === 'lib-create-folder') {
                lx1 = boxLeft;
                ly1 = boxTop + boxHeight / 2;
            }

            const dx = cx - lx1;
            const dy = cy - ly1;
            const dist = Math.sqrt(dx * dx + dy * dy);

            // Default to center if too close
            let lx2 = cx;
            let ly2 = cy;

            if (dist > 30) {
                // The highlight pulse is an ellipse with these radii
                // Use dynamic padding: tighter for small elements, more generous for wide ones to cover corners
                const p = is3DTarget ? 24 : Math.min(20, Math.max(4, rect.width * 0.1));
                const a = rect.width / 2 + p;
                const b = rect.height / 2 + p;

                // Unit vector from center back towards tooltip
                const ux = -dx / dist;
                const uy = -dy / dist;

                // Distance to edge of ellipse (x/a)^2 + (y/b)^2 = 1 along direction (ux, uy)
                const radius = 1 / Math.sqrt((ux * ux) / (a * a) + (uy * uy) / (b * b));

                if (dist > radius + 5) {
                    lx2 = cx - (dx / dist) * radius;
                    ly2 = cy - (dy / dist) * radius;
                }
            }

            const isTorsoStep = currentStep?.id === 'adv-torso-intro' ||
                currentStep?.id === 'adv-torso-t' ||
                currentStep?.id === 'adv-torso-v' ||
                currentStep?.id === 'adv-torso-l';

            if (isTorsoStep && currentStep?.id !== 'adv-torso-intro') {
                const p = Math.min(20, Math.max(4, rect.width * 0.1));
                const a = rect.width / 2 + p;
                lx2 = cx + a;
                ly2 = cy;
            }

            return { x1: lx1, y1: ly1, x2: lx2, y2: ly2 };
        });
    };

    const actionLines = calculateLines(targetRects);
    const clueLines = calculateLines(supportingRects);


    // Helper to render message with simple markdown bold support and color tags
    const renderStyledMessage = (text: string) => {
        if (!text) return null;
        
        // Regex to split by bold (**bold**) and color tags ([color:name](text))
        const regex = /(\*\*.*?\*\*|\[color:.*?\]\(.*?\))/g;
        const parts = text.split(regex);
        
        return parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return (
                    <strong
                        key={i}
                        style={{
                            color: '#fff',
                            fontWeight: 700,
                            textShadow: '0 0 10px rgba(255,255,255,0.2)'
                        }}
                    >
                        {part.slice(2, -2)}
                    </strong>
                );
            }
            if (part.startsWith('[color:') && part.endsWith(')')) {
                const colorMatch = part.match(/\[color:(.*?)\]\((.*?)\)/);
                if (colorMatch) {
                    const [, color, content] = colorMatch;
                    let mappedColor = color;
                    if (color === 'red') mappedColor = '#f87171'; // premium soft red
                    if (color === 'cyan' || color === 'green/cyan') mappedColor = '#38bdf8'; // sky blue / cyan
                    if (color === 'yellow') mappedColor = '#facc15'; // warm amber/yellow
                    
                    return (
                        <span
                            key={i}
                            style={{
                                color: mappedColor,
                                fontWeight: 700,
                                textShadow: `0 0 8px ${mappedColor}40`
                            }}
                        >
                            {content}
                        </span>
                    );
                }
            }
            return part;
        });
    };

    return (
        <>
            <div
                className="tutorial-tooltip-container"
                ref={tooltipRef}
                style={{
                    position: 'fixed',
                    top: `${top}px`,
                    left: `${left}px`,
                    width: `${tooltipWidth}px`,
                    zIndex: 30000,
                    pointerEvents: isPositionStable ? 'auto' : 'none',
                    opacity: (isPositionStable && !isAnyModalOpen) ? 1 : 0,
                    transition: 'top 0.35s cubic-bezier(0.4, 0, 0.2, 1), left 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease'
                }}
            >
                <div 
                    className={`glass-panel ${isFinalStep ? 'tutorial-final-step' : ''}`}
                    style={{
                        background: 'rgba(15, 23, 42, 0.92)',
                        border: isFinalStep ? undefined : '2px solid rgba(255, 255, 255, 0.8)',
                        padding: '10px 14px',
                        boxShadow: isFinalStep ? undefined : '0 20px 50px rgba(0,0,0,0.8), 0 0 30px rgba(255, 255, 255, 0.2)',
                        pointerEvents: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        borderRadius: '12px',
                        opacity: isContentVisible ? 1 : 0,
                        transition: 'opacity 0.2s ease',
                        minHeight: '80px'
                    }}
                >
                    {displayedStep && (
                        <>
                            <div className="flex-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '6px', marginBottom: '2px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#38bdf8' }}>
                                    <GraduationCap size={16} />
                                    <span style={{ fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        {t('tutorial.step_count', {
                                            current: tutorialProgress.currentStepIndex + 1,
                                            total: currentModule?.steps.length || 0,
                                            defaultValue: `STEP ${tutorialProgress.currentStepIndex + 1} OF ${currentModule?.steps.length || 0}`
                                        })}
                                    </span>
                                </div>
                                <button
                                    onClick={() => updateTutorialProgress({ currentModuleId: null, currentStepIndex: 0 })}
                                    style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: '2px' }}
                                >
                                    <X size={14} />
                                </button>
                            </div>

                            <div style={{ fontSize: '0.85rem', color: 'white', lineHeight: '1.4', fontWeight: 450, whiteSpace: 'pre-wrap' }}>
                                {renderStyledMessage(displayedStep.message)}
                            </div>

                            {displayedStep.hint && (
                                <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', fontStyle: 'italic', marginTop: '4px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '6px', whiteSpace: 'pre-wrap' }}>
                                    {renderStyledMessage(displayedStep.hint)}
                                </div>
                            )}

                            {displayedStep.showVisual === 'viewControls' && (
                                <div style={{ padding: '2px 0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <div className="flex-row" style={{ gap: '8px', fontSize: '0.72rem', alignItems: 'center' }}>
                                        <div style={{ width: '52px', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                                            <div className="mouse-drag-visual drag-left-only wiggle" style={{ transform: 'scale(0.8)', transformOrigin: 'right' }} />
                                        </div>
                                        <span style={{ color: 'rgba(255,255,255,0.7)', lineHeight: '1.2' }}>{t('tutorial.pan')}: <strong style={{ color: '#fff' }}>{t('tutorial.left')}</strong> + {t('tutorial.drag')}</span>
                                    </div>
                                    <div className="flex-row" style={{ gap: '8px', fontSize: '0.72rem', alignItems: 'center' }}>
                                        <div className="flex-row" style={{ width: '52px', gap: '4px', justifyContent: 'flex-end', flexShrink: 0 }}>
                                            <div className="mouse-drag-visual press-middle wiggle" style={{ transform: 'scale(0.8)', transformOrigin: 'right' }} />
                                        </div>
                                        <span style={{ color: 'rgba(255,255,255,0.7)', lineHeight: '1.2' }}>{t('tutorial.rotate')}: <strong style={{ color: '#fff' }}>{t('tutorial.scroll_press')}</strong> + {t('tutorial.move')}</span>
                                    </div>
                                    <div className="flex-row" style={{ gap: '8px', fontSize: '0.72rem', alignItems: 'center' }}>
                                        <div className="flex-row" style={{ width: '52px', gap: '4px', justifyContent: 'flex-end', flexShrink: 0 }}>
                                            <div className="keyboard-key-visual" style={{ width: '28px', fontSize: '7.5px', height: '18px', animation: 'none', background: 'rgba(56, 189, 248, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>SHFT</div>
                                            <div className="mouse-drag-visual drag-left-only wiggle" style={{ transform: 'scale(0.8)', transformOrigin: 'right' }} />
                                        </div>
                                        <span style={{ color: 'rgba(255,255,255,0.7)', lineHeight: '1.2' }}>{t('tutorial.rotate')}: <strong style={{ color: '#fff' }}>{t('tutorial.shift_left')}</strong> + {t('tutorial.move')}</span>
                                    </div>
                                    <div className="flex-row" style={{ gap: '8px', fontSize: '0.72rem', alignItems: 'center' }}>
                                        <div style={{ width: '52px', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                                            <div className="mouse-drag-visual scroll-only" style={{ transform: 'scale(0.8)', transformOrigin: 'right' }} />
                                        </div>
                                        <span style={{ color: 'rgba(255,255,255,0.7)', lineHeight: '1.2' }}>{t('tutorial.zoom')}: <strong style={{ color: '#fff' }}>{t('tutorial.scroll')}</strong></span>
                                    </div>
                                </div>
                            )}

                            {displayedStep.showVisual === 'scrollRotation' && (
                                <>
                                    <div style={{ padding: '6px 0', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <div className="flex-row" style={{ gap: '14px', fontSize: '0.75rem', alignItems: 'center' }}>
                                            <div className="flex-row" style={{ width: '80px', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
                                                <div className="keyboard-key-visual" style={{ width: '24px', height: '20px', fontSize: '9px', animation: 'none', background: 'rgba(56, 189, 248, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Z</div>
                                                <span style={{ color: '#38bdf8', fontWeight: 900, fontSize: '12px' }}>+</span>
                                            </div>
                                            <div className="flex-row" style={{ gap: '8px', alignItems: 'center' }}>
                                                <div className="mouse-drag-visual scroll-only" style={{ transform: 'scale(0.8)', transformOrigin: 'right', display: 'inline-block' }} />
                                                <span style={{ color: 'rgba(255,255,255,0.7)', marginLeft: '10px' }}>{t('tutorial.rotate')}: <strong style={{ color: '#fff' }}>{t('tutorial.scroll')}</strong></span>
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '0.85rem', color: 'white', lineHeight: '1.4', fontWeight: 450, whiteSpace: 'pre-wrap', marginTop: '12px' }}>
                                        {renderStyledMessage("Click **Next Step** to continue.")}
                                    </div>
                                </>
                            )}

                        </>
                    )}

                    {(currentStep.actionRequired !== 'none' || isFinalStep) && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px',
                            marginTop: '8px',
                            paddingTop: '8px',
                            borderTop: '1px solid rgba(255,255,255,0.08)',
                            width: '100%'
                        }}>
                            {isFinalStep ? (
                                <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '8px' }}>
                                        <button
                                            onClick={() => updateTutorialProgress({ currentModuleId: null, currentStepIndex: 0 })}
                                            style={{
                                                padding: '4px 12px',
                                                background: 'rgba(255, 255, 255, 0.1)',
                                                color: '#e2e8f0',
                                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontWeight: 600,
                                                fontSize: '0.75rem',
                                                flexGrow: nextModule ? 0 : 1,
                                                textAlign: 'center',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                                        >
                                            {t('tutorial.later', { defaultValue: 'Later' })}
                                        </button>
                                        {nextModule && (
                                            <button
                                                className="primary"
                                                onClick={() => startNextModule(nextModule.id)}
                                                style={{
                                                    padding: '4px 12px',
                                                    background: '#38bdf8',
                                                    color: '#0f172a',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold',
                                                    fontSize: '0.75rem',
                                                    flexGrow: 1,
                                                    textAlign: 'center',
                                                    boxShadow: '0 0 15px rgba(56, 189, 248, 0.4)',
                                                    transition: 'all 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.background = '#0ea5e9';
                                                    e.currentTarget.style.boxShadow = '0 0 20px rgba(14, 165, 233, 0.6)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.background = '#38bdf8';
                                                    e.currentTarget.style.boxShadow = '0 0 15px rgba(56, 189, 248, 0.4)';
                                                }}
                                            >
                                                {t('common.next', { defaultValue: 'Next' })}
                                            </button>
                                        )}
                                    </div>
                                    {nextModule && (
                                        <div 
                                            style={{
                                                fontSize: '0.72rem',
                                                color: '#38bdf8',
                                                fontWeight: 600,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '4px',
                                                width: '100%',
                                                marginTop: '2px'
                                            }}
                                        >
                                            <span>{t('tutorial.module_label', { name: nextModule.title, defaultValue: `Module : ${nextModule.title}` })}</span>
                                            <span style={{ fontSize: '0.85rem' }}>→</span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%' }}>
                                    <MousePointer2 size={12} className="spin-icon" style={{ animationDuration: '3s', color: '#38bdf8' }} />
                                    {currentStep.actionRequired === 'completeModule' ? (
                                        <button
                                            className="primary"
                                            onClick={advanceStep}
                                            style={{
                                                padding: '4px 12px',
                                                width: '100%',
                                                fontWeight: 'bold',
                                                fontSize: '0.75rem'
                                            }}
                                        >
                                            {t('tutorial.finish_tutorial')}
                                        </button>
                                    ) : currentStep.actionRequired === 'next' ? (
                                        <button
                                            className="primary"
                                            onClick={advanceStep}
                                            style={{
                                                padding: '4px 12px',
                                                width: '100%',
                                                fontWeight: 'bold',
                                                fontSize: '0.75rem'
                                            }}
                                        >
                                            {t('tutorial.next_step')}
                                        </button>
                                    ) : (
                                        <div className="flex-row" style={{ gap: '8px', fontSize: '0.75rem', color: 'rgba(56,189,248,0.7)', fontStyle: 'italic', fontWeight: '500', width: '100%' }}>
                                            <span>{t('tutorial.waiting')}</span>
                                            {hasVisual && (
                                                <div className="flex-row" style={{ marginLeft: 'auto', gap: '6px', transform: 'scale(0.7)' }}>
                                                    {(currentStep.actionRequired === 'rotateJointMouse' ||
                                                        currentStep.actionRequired === 'rotateConicalMouse' ||
                                                        currentStep.actionRequired === 'rotateTorsoMouse' ||
                                                        currentStep.actionRequired === 'moveFloorMouse' ||
                                                        currentStep.actionRequired === 'changeElevation' ||
                                                        currentStep.id === 'wm-scroll-rotation' ||
                                                        currentStep.id === 'mark-location-shortcut') &&
                                                        currentStep.id !== 'adv-drag-elbow-drag' && (
                                                             <>
                                                                 <div className={`keyboard-key-visual ${currentStep.id === 'rotate-shoulder-mouse' ? 'key-sync' : ''}`}>
                                                                     {currentStep.actionRequired === 'rotateConicalMouse' ? 'A' :
                                                                         currentStep.actionRequired === 'rotateTorsoMouse' ? 'F' :
                                                                         currentStep.actionRequired === 'moveFloorMouse' ? 'D' :
                                                                             currentStep.actionRequired === 'changeElevation' ? 'S' :
                                                                                 currentStep.id === 'mark-location-shortcut' ? 'L' :
                                                                                     currentStep.targetValue === 'orientation' ? 'Y' : 'Z'}
                                                                 </div>
                                                                 <span style={{ color: '#38bdf8', fontWeight: 900, fontSize: '14px' }}>+</span>
                                                             </>
                                                         )}
                                                    <div style={{ position: 'relative', width: '40px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        {currentStep.id !== 'mark-location-shortcut' && <div className="track-line" />}
                                                        <div className={`mouse-drag-visual ${
                                                            currentStep.id === 'wm-scroll-rotation' ? 'scroll-only' :
                                                            currentStep.id === 'mark-location-shortcut' ? 'click-only' :
                                                            (currentStep.id === 'rotate-shoulder-z' || currentStep.id === 'adv-drag-elbow-drag' ? 'drag-right-only' :
                                                            (currentStep.id === 'rotate-shoulder-mouse' ? 'drag-left-only' :
                                                            (currentStep.actionRequired === 'changeElevation' ? 'drag-updown' : '')))
                                                        }`} />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Aesthetically matching line with arrow */}
            {isPositionStable && isIndicatorsVisible && (targetRect || currentStep?.id === 'adv-drag-relationship-intro') && !isPosing && (
                <svg
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        pointerEvents: 'none',
                        zIndex: 29998
                    }}
                >
                    <defs>
                        <marker
                            id="tutorial-arrowhead"
                            markerWidth="10"
                            markerHeight="8"
                            refX="10"
                            refY="4"
                            orient="auto"
                        >
                            <path d="M0,0 L10,4 L0,8 Z" fill="#38bdf8" />
                        </marker>
                        <marker
                            id="tutorial-arrowhead-red"
                            markerWidth="10"
                            markerHeight="8"
                            refX="10"
                            refY="4"
                            orient="auto"
                        >
                            <path d="M0,0 L10,4 L0,8 Z" fill="#f87171" />
                        </marker>
                    </defs>

                    {/* Supporting clue lines: subdued, straight, no animation */}
                    {clueLines.map((line, idx) => (
                        <line
                            key={`clue-${idx}`}
                            x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2}
                            stroke="rgba(255, 255, 255, 0.6)"
                            strokeWidth="1.2"
                        />
                    ))}

                    {/* primary action lines: bold, dashed, animated with arrowhead */}
                    {currentStep?.id !== 'seq-music-waveform' &&
                     currentStep?.id !== 'seq-music-grid' &&
                     currentStep?.id !== 'seq-music-dot' &&
                     currentStep?.id !== 'seq-music-subdivision-effect' &&
                     currentStep?.id !== 'adv-torso-conclusion' &&
                     actionLines.map((line, idx) => (
                        <line
                            key={`action-${idx}`}
                            x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2}
                            stroke="#38bdf8"
                            strokeWidth="2.5"
                            markerEnd="url(#tutorial-arrowhead)"
                            strokeDasharray="6,4"
                            style={{
                                animation: 'tutorial-line-flow 1s linear infinite'
                            }}
                        />
                    ))}

                    {/* Render custom second arrow for adv-labels-explanation step pointing to sidebar */}
                    {(() => {
                        if (currentStep?.id === 'adv-labels-explanation') {
                            const sidebarEl = document.getElementById('joint-heading-leftShoulder');
                            if (sidebarEl) {
                                const sidebarRect = sidebarEl.getBoundingClientRect();
                                const startX = left + tooltipWidth;
                                const startY = top + tooltipHeight / 2;
                                const endX = sidebarRect.left - 12;
                                const endY = sidebarRect.top + sidebarRect.height / 2;
                                return (
                                    <line
                                        x1={startX}
                                        y1={startY}
                                        x2={endX}
                                        y2={endY}
                                        stroke="#38bdf8"
                                        strokeWidth="2.5"
                                        markerEnd="url(#tutorial-arrowhead)"
                                        strokeDasharray="6,4"
                                        style={{
                                            animation: 'tutorial-line-flow 1s linear infinite'
                                        }}
                                    />
                                );
                            }
                        }
                        return null;
                    })()}

                    {/* Render custom arrows for Step 5 pointing to child and parent joints */}
                    {(() => {
                        if (currentStep?.id === 'adv-drag-relationship-intro') {
                            const childEl = document.getElementById('tutorial-child-joint-target');
                            const parentEl = document.getElementById('tutorial-parent-joint-target');
                            const lines: React.ReactNode[] = [];

                            if (childEl) {
                                const rect = childEl.getBoundingClientRect();
                                const startX = left + tooltipWidth;
                                const startY = top + tooltipHeight * 0.75;
                                const endX = rect.left + rect.width / 2;
                                const endY = rect.top + rect.height / 2;
                                lines.push(
                                    <line
                                        key="child-arrow"
                                        x1={startX} y1={startY} x2={endX} y2={endY}
                                        stroke="#38bdf8" strokeWidth="2.5"
                                        markerEnd="url(#tutorial-arrowhead)" strokeDasharray="6,4"
                                        style={{ animation: 'tutorial-line-flow 1s linear infinite' }}
                                    />
                                );
                            }

                            if (parentEl) {
                                const rect = parentEl.getBoundingClientRect();
                                const startX = left + tooltipWidth;
                                const startY = top + tooltipHeight * 0.25;
                                const endX = rect.left + rect.width / 2;
                                const endY = rect.top + rect.height / 2;
                                lines.push(
                                    <line
                                        key="parent-arrow"
                                        x1={startX} y1={startY} x2={endX} y2={endY}
                                        stroke="#f87171" strokeWidth="2.5"
                                        markerEnd="url(#tutorial-arrowhead-red)" strokeDasharray="6,4"
                                        style={{ animation: 'tutorial-line-flow 1s linear infinite' }}
                                    />
                                );
                            }

                            return lines;
                        }
                        return null;
                    })()}

                    {/* Render custom second arrow for lib-create-folder step pointing to the New Folder button */}
                    {(() => {
                        if (currentStep?.id === 'lib-create-folder') {
                            const newFolderBtn = document.getElementById('library-new-root-folder-btn');
                            if (newFolderBtn) {
                                const btnRect = newFolderBtn.getBoundingClientRect();
                                const startX = btnRect.left < left ? left : left + tooltipWidth;
                                const startY = top + tooltipHeight / 2;
                                const btnCenterX = btnRect.left + btnRect.width / 2;
                                const btnCenterY = btnRect.top + btnRect.height / 2;
                                const dx = btnCenterX - startX;
                                const dy = btnCenterY - startY;
                                const dist = Math.sqrt(dx * dx + dy * dy);
                                const endX = btnCenterX - (dx / dist) * 16;
                                const endY = btnCenterY - (dy / dist) * 16;
                                return (
                                    <line
                                        x1={startX}
                                        y1={startY}
                                        x2={endX}
                                        y2={endY}
                                        stroke="#38bdf8"
                                        strokeWidth="2.5"
                                        markerEnd="url(#tutorial-arrowhead)"
                                        strokeDasharray="6,4"
                                        style={{
                                            animation: 'tutorial-line-flow 1s linear infinite'
                                        }}
                                    />
                                );
                            }
                        }
                        return null;
                    })()}

                    {/* Render custom arrows for individual soundtrack breakdown steps */}
                    {(() => {
                        if (currentStep?.id === 'seq-music-waveform') {
                            const canvasEl = document.getElementById('timeline-waveform-canvas');
                            if (canvasEl) {
                                const canvasRect = canvasEl.getBoundingClientRect();
                                const targetX = canvasRect.left + 150;
                                const targetY = canvasRect.top + canvasRect.height / 3;
                                const startX = left + tooltipWidth / 2; // bottom edge center
                                const startY = top + tooltipHeight;
                                return (
                                    <line
                                        x1={startX} y1={startY} x2={targetX} y2={targetY}
                                        stroke="#38bdf8" strokeWidth="2.5"
                                        markerEnd="url(#tutorial-arrowhead)" strokeDasharray="6,4"
                                        style={{ animation: 'tutorial-line-flow 1s linear infinite' }}
                                    />
                                );
                            }
                        }
                        if (currentStep?.id === 'seq-music-grid') {
                            const canvasEl = document.getElementById('timeline-waveform-canvas');
                            if (canvasEl) {
                                const canvasRect = canvasEl.getBoundingClientRect();
                                const tickEl = document.getElementById('timeline-frame-2.000');
                                if (tickEl) {
                                    const tickRect = tickEl.getBoundingClientRect();
                                    const targetX = tickRect.left; // Point exactly to the vertical border line at 2.0s
                                    const targetY = canvasRect.top + canvasRect.height * 0.5; // Centered vertically on the waveform grid
                                    const startX = left + tooltipWidth; // right edge center
                                    const startY = top + tooltipHeight / 2;
                                    return (
                                        <line
                                            x1={startX} y1={startY} x2={targetX} y2={targetY}
                                            stroke="#38bdf8" strokeWidth="2.5"
                                            markerEnd="url(#tutorial-arrowhead)" strokeDasharray="6,4"
                                            style={{ animation: 'tutorial-line-flow 1s linear infinite' }}
                                        />
                                    );
                                }
                            }
                        }

                        if (currentStep?.id === 'seq-music-dot') {
                            const dotEl = document.getElementById('timeline-measure-dot-2.000');
                            if (dotEl) {
                                const dotRect = dotEl.getBoundingClientRect();
                                const centerX = dotRect.left + dotRect.width / 2;
                                const centerY = dotRect.top + dotRect.height / 2;
                                const startX = dotRect.left < left ? left : left + tooltipWidth;
                                const startY = top + tooltipHeight / 4;

                                const dx = centerX - startX;
                                const dy = centerY - startY;
                                const length = Math.sqrt(dx * dx + dy * dy) || 1;

                                const p = Math.min(20, Math.max(4, dotRect.width * 0.1));
                                const radius = (dotRect.width / 2) + p;
                                // Subtract the outer circle radius so the arrow tip stops exactly on the outer circle line
                                const stopDistance = radius;
                                const endX = centerX - (dx / length) * stopDistance;
                                const endY = centerY - (dy / length) * stopDistance;

                                return (
                                    <line
                                        x1={startX} y1={startY} x2={endX} y2={endY}
                                        stroke="#38bdf8" strokeWidth="2.5"
                                        markerEnd="url(#tutorial-arrowhead)" strokeDasharray="6,4"
                                        style={{ animation: 'tutorial-line-flow 1s linear infinite' }}
                                    />
                                );
                            }
                        }
                        return null;
                    })()}
                </svg>
            )}

            {/* Highlight pulses around all targets */}
            {/* Primary Action highlight pulses */}
            {isPositionStable && isIndicatorsVisible && !isPosing && 
                currentStep?.id !== 'shortcuts-stage' && 
                currentStep?.id !== 'seq-music-waveform' && 
                currentStep?.id !== 'seq-music-grid' && 
                currentStep?.id !== 'seq-music-subdivision-effect' && 
                targetRects.map((rect, idx) => {
                const p = is3DTarget ? 24 : Math.min(20, Math.max(4, rect.width * 0.1));
                return (
                    <div
                        key={`action-pulse-${idx}`}
                        style={{
                            position: 'fixed',
                            top: `${rect.top - p}px`,
                            left: `${rect.left - p}px`,
                            width: `${rect.width + p * 2}px`,
                            height: `${rect.height + p * 2}px`,
                            border: '2px solid #38bdf8',
                            borderRadius: '50%',
                            pointerEvents: 'none',
                            zIndex: 29999,
                            animation: 'tutorial-pulse 2s infinite ease-in-out',
                            boxShadow: '0 0 30px rgba(56, 189, 248, 0.6)',
                            boxSizing: 'border-box'
                        }}
                    />
                );
            })}

            {/* Supporting Clue highlight: thin, non-pulsing, subdued */}
            {isPositionStable && isIndicatorsVisible && !isPosing && supportingRects.map((rect, idx) => (
                <div
                    key={`clue-highlight-${idx}`}
                    style={{
                        position: 'fixed',
                        top: `${rect.top - 4}px`,
                        left: `${rect.left - 4}px`,
                        width: `${rect.width + 8}px`,
                        height: `${rect.height + 8}px`,
                        border: '1.5px solid rgba(255, 255, 255, 0.5)',
                        borderRadius: '4px',
                        pointerEvents: 'none',
                        zIndex: 29997,
                        boxSizing: 'border-box'
                    }}
                />
            ))}
        </>
    );
};
